import { notAvailableRolesToCreateFormatted, rolesToCreateFormatted } from '@utils/formatter';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { IUserEntity } from '@interfaces/user/user';
import { IRolesUserProfile } from '@interfaces/user/userProfile';

import { EUserRoles } from '@enums/common';

import { adminRoles } from '@components/ModalComponents/UserForm/constants';

export const generateSelectedButNotAvailableRoles = (
	editableUser?: IUserEntity,
	rolesToCreate?: Array<TSelectOption>,
): TSelectOption[] => editableUser?.roles?.reduce<Array<TSelectOption>>((acc, role) => {
	if (!rolesToCreate?.some((element: TSelectOption) => role?.name === element?.name)) {
		//add assetOwnerDefaultContact for editable roles
		if (role.name === EUserRoles.assetOwnerDefaultContact) {
			acc.push({ ...role, isFixed: false });
		} else {
			acc.push({ ...role, isFixed: true });
		}
	}

	return acc;
}, []) || [];

export const filterUsersAdminRoleByOrgType = (
	rolesToCreate: Array<TSelectOption<string>>,
	currentOrganisationTypes?: string[],
): TSelectOption<string>[] => {
	if (!currentOrganisationTypes?.length) {
		return rolesToCreate;
	}

	const relevantAdminRoleNames = adminRoles
		.filter(({ organizationType }) => currentOrganisationTypes.includes(organizationType))
		.map(({ role }) => role);

	return rolesToCreate.filter((role) => {
		const isAdminRole = adminRoles.some(admin => admin.role === role?.name);

		if (!isAdminRole) {
			return true;
		}

		return role?.name && relevantAdminRoleNames.includes(role.name);
	});
};

export const generateRolesForCreateUser = ({
	editableUser,
	rolesToCreate,
	currentOrganisationTypes,
	isExistUserAdmin,
}: {
	editableUser?: IUserEntity;
	rolesToCreate: Array<TSelectOption<string>>;
	currentOrganisationTypes?: string[];
	isExistUserAdmin: boolean;
}) => {
	const selectedButNotAvailableRoles = generateSelectedButNotAvailableRoles(editableUser, rolesToCreate);
	const isSomeOrganisationTypes = !!currentOrganisationTypes?.length && currentOrganisationTypes.length > 1;

	const filteredRoles = filterUsersAdminRoleByOrgType(rolesToCreate, currentOrganisationTypes);

	return [
		...notAvailableRolesToCreateFormatted(selectedButNotAvailableRoles),
		...rolesToCreateFormatted(
			filteredRoles,
			isExistUserAdmin,
			isSomeOrganisationTypes,
		),
	];

};

export const getRoleSelectionHelper = ({
	multiSelectOptions,
	allRoles,
	currentOrganisationTypes,
}: {
	multiSelectOptions: Array<TSelectOption<string>>;
	allRoles: Array<TSelectOption>;
	currentOrganisationTypes?: string[];
}) => {
	const relevantAdminRoles = adminRoles.filter(({ organizationType }) => {
		return currentOrganisationTypes?.includes(organizationType);
		},
	);
	const relevantAdminRoleNames = relevantAdminRoles.map(({ role }) => role);
	const selectedAdminRoles = multiSelectOptions.filter((option) =>
		relevantAdminRoles.some((role) => role.role === option?.name),
	);

	const isSomeAdminRolesRequired =
		selectedAdminRoles.length > 0 &&
		selectedAdminRoles.length < relevantAdminRoles.length;

	const getRolesWithoutRelevantAdmins = () => {
		return multiSelectOptions.filter(
			(option) => !!option?.name && !relevantAdminRoleNames.includes(option.name),
		);
	};

	const getRolesWithRelevantAdmins = () => {
		const additionalAdminRoles = allRoles.filter(
			(role) =>
				role?.name && relevantAdminRoleNames.includes(role.name) &&
				!multiSelectOptions.some((selected) => selected?.name === role.name),
		);

		return [...multiSelectOptions, ...additionalAdminRoles];
	};

	return {
		getRolesWithoutRelevantAdmins,
		getRolesWithRelevantAdmins,
		isSomeAdminRolesRequired,
	};
};

export const prepareUserRolesForUserProfile = (roles: Array<IRolesUserProfile>): {
	name: string;
	disabled: boolean;
}[] => {
	return roles.map(({ role: { title }, isRevoked }) => {
		return ({
			name: title,
			disabled: isRevoked,
		});
	});
};
